import {Suspense} from 'react';
import {diApi} from '@di-shared/api/di';
import {AccountBookApiResponseLong, AccountBookApiResponseUserSettingsResponse} from '@di-shared/api/di/__generated';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {ErrorBoundary} from '@sentry/react';
import {getQueryKey} from '@shared/queryKeyMap';
import {WebviewUtils} from '@shared/webviewUtils';
import {useSuspenseQueries} from '@tanstack/react-query';

function CapitalAutoCollectedBanner(): JSX.Element {
  const {t} = useInferableTranslation();

  return (
    <ErrorBoundary fallback={<></>}>
      <Suspense
        fallback={
          <div
            className="driving_banner type_entry skeleton"
            style={{display: 'block'}}
          >
            <a
              role="button"
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_r">{t('T40282_$count', {count: 0})}</strong>
                <span className="driving_banner_desc body_large_b">
                  {t('T40283')} <span className="ic ic_arrow_forward_16px"></span>
                </span>
              </span>
            </a>
          </div>
        }
      >
        <Inner />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner(): JSX.Element {
  const {t} = useInferableTranslation();

  const [{data: pendingTransactionCount}, {data: isBannerNotificationsEnabled}] = useSuspenseQueries({
    queries: [
      {
        queryKey: [getQueryKey(diApi.accountBookPendingTransaction.countPendingTransactions)],
        queryFn: () => diApi.accountBookPendingTransaction.countPendingTransactions(),
        select: (data: AccountBookApiResponseLong) => data.data ?? 0,
      },
      {
        queryKey: [getQueryKey(diApi.accountBookPendingTransaction.getSettings)],
        queryFn: () => diApi.accountBookPendingTransaction.getSettings(),
        select: (data: AccountBookApiResponseUserSettingsResponse) => data.data?.pushNotificationEnabled ?? false,
      },
    ],
  });

  if (!isBannerNotificationsEnabled || pendingTransactionCount === 0) {
    return <></>;
  }

  function openAutoCollectedPage() {
    WebviewUtils.openNewWebView({
      url: `${import.meta.env.VITE_CCI_DOMAIN}/account-book/transaction/auto-collected`,
      isModal: true,
      isNavigationBar: true,
      isToolBar: false,
    });
  }

  return (
    <div
      className="driving_banner type_entry"
      style={{display: 'block'}}
    >
      <a
        role="button"
        className="driving_banner_box"
        onClick={() => openAutoCollectedPage()}
      >
        <span className="driving_banner_txtbox">
          <strong className="driving_banner_title body_large_r">{t('T40282_$count', {count: pendingTransactionCount})}</strong>
          <span className="driving_banner_desc body_large_b">
            {t('T40283')} <span className="ic ic_arrow_forward_16px"></span>
          </span>
        </span>
      </a>
    </div>
  );
}

export default CapitalAutoCollectedBanner;
